/**
 * Leviathan Web - New Shipment Page
 * Quasar Prime LLC
 * Create new shipment with BOL generation
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ShoppingCart,
  Truck,
  User,
  Calendar,
  Package,
  AlertTriangle,
  FileText,
  Database,
  Plus,
  Minus,
  Building2,
  ExternalLink,
  Fuel,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { API_URL } from '@/config/api';

interface OrderItem {
  id: string;
  quantity: number;
  deliveredQuantity: number;
  unit: string;
  unitPrice: number;
  alreadyShipped?: number;
  remainingQuantity?: number;
  product: {
    id: string;
    name: string;
    sku: string;
    unit: string;
    pricePerUnit: string | number | null;
  };
}

interface Order {
  id: string;
  orderNumber: string;
  total: number;
  priority: string;
  requestedDate: string | null;
  deliveryAddress: string | null;
  deliveryCity: string | null;
  deliveryState: string | null;
  deliveryZip: string | null;
  customer: {
    id: string;
    name: string;
    customerNumber: string;
  };
  items: OrderItem[];
  hasRemainingQuantity?: boolean;
}

interface TruckOption {
  id: string;
  unitNumber: string;
  name: string | null;
  totalCapacity: number | null;
  status: string;
}

interface DriverOption {
  id: string;
  driverNumber: string;
  firstName: string;
  lastName: string;
  status: string;
  phone: string | null;
}

interface Tank {
  id: string;
  tankNumber: string;
  name: string;
  currentLevel: number;
  capacity: number;
  product: {
    id: string;
    name: string;
  } | null;
}

interface ShipmentItemInput {
  productId: string;
  productName: string;
  orderedQuantity: number;
  shippedQuantity: number;
  unitPrice: number;
  unit: string;
}

interface TerminalOption {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
  terminalCode: string | null;
}

const NewShipmentPage = () => {
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [orders, setOrders] = useState<Order[]>([]);
  const [trucks, setTrucks] = useState<TruckOption[]>([]);
  const [drivers, setDrivers] = useState<DriverOption[]>([]);
  const [tanks, setTanks] = useState<Tank[]>([]);
  const [terminals, setTerminals] = useState<TerminalOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [shipmentType, setShipmentType] = useState<'INTERNAL' | 'THIRD_PARTY' | 'CUSTOMER_PICKUP'>('INTERNAL');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [selectedTruck, setSelectedTruck] = useState<TruckOption | null>(null);
  const [selectedDriver, setSelectedDriver] = useState<DriverOption | null>(null);
  const [selectedTank, setSelectedTank] = useState<Tank | null>(null);
  // 'Lifted at' — see the FormSection below for why this is on the create form.
  const [originTerminalId, setOriginTerminalId] = useState('');
  const [shipDate, setShipDate] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryCity, setDeliveryCity] = useState('');
  const [deliveryState, setDeliveryState] = useState('');
  const [deliveryZip, setDeliveryZip] = useState('');
  const [notes, setNotes] = useState('');
  const [shipmentItems, setShipmentItems] = useState<ShipmentItemInput[]>([]);

  // Third-party carrier fields
  const [carrierName, setCarrierName] = useState('');
  const [carrierBol, setCarrierBol] = useState('');
  const [externalDriver, setExternalDriver] = useState('');
  const [externalVehicle, setExternalVehicle] = useState('');
  const [pickedUpBy, setPickedUpBy] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [ordersRes, trucksRes, driversRes, tanksRes, terminalsRes] = await Promise.all([
          fetch(`${API_URL}/api/shipments/orders`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          }),
          fetch(`${API_URL}/api/trucks/available`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          }),
          fetch(`${API_URL}/api/drivers/available`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          }),
          fetch(`${API_URL}/api/tanks?status=ACTIVE`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          }),
          fetch(`${API_URL}/api/terminals?limit=200`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          }),
        ]);

        if (ordersRes.ok) {
          const data = await ordersRes.json();
          setOrders(Array.isArray(data) ? data : data.orders || []);
        }

        if (trucksRes.ok) {
          const data = await trucksRes.json();
          setTrucks(Array.isArray(data) ? data : data.trucks || []);
        }

        if (driversRes.ok) {
          const data = await driversRes.json();
          setDrivers(Array.isArray(data) ? data : data.drivers || []);
        }

        if (tanksRes.ok) {
          const data = await tanksRes.json();
          setTanks(Array.isArray(data) ? data : data.tanks || []);
        }

        if (terminalsRes.ok) {
          const data = await terminalsRes.json();
          setTerminals(Array.isArray(data) ? data : data.items || data.terminals || []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load shipment data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    // Set default date to today
    const today = new Date().toISOString().split('T')[0] || '';
    setShipDate(today);
  }, [accessToken]);

  // When order is selected, populate items and delivery address
  useEffect(() => {
    if (selectedOrder) {
      // Set delivery address from order
      setDeliveryAddress(selectedOrder.deliveryAddress || '');
      setDeliveryCity(selectedOrder.deliveryCity || '');
      setDeliveryState(selectedOrder.deliveryState || '');
      setDeliveryZip(selectedOrder.deliveryZip || '');

      // Populate shipment items from order items
      const items: ShipmentItemInput[] = selectedOrder.items.map((item) => ({
        productId: item.product.id,
        productName: item.product.name,
        orderedQuantity: item.remainingQuantity ?? item.quantity,
        shippedQuantity: item.remainingQuantity ?? item.quantity,
        unitPrice: item.unitPrice,
        unit: item.unit,
      }));
      setShipmentItems(items);
    } else {
      setShipmentItems([]);
    }
  }, [selectedOrder]);

  const handleItemQuantityChange = (index: number, quantity: number) => {
    const newItems = [...shipmentItems];
    if (newItems[index]) {
      newItems[index].shippedQuantity = Math.max(0, quantity);
    }
    setShipmentItems(newItems);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!selectedOrder || !shipDate || shipmentItems.length === 0) {
      setError('Please select an order, ship date, and ensure items are selected');
      return;
    }

    // Validate at least one item has quantity
    const hasQuantity = shipmentItems.some((item) => item.shippedQuantity > 0);
    if (!hasQuantity) {
      setError('At least one item must have a quantity greater than 0');
      return;
    }

    // Validate third-party carrier fields
    if (shipmentType === 'THIRD_PARTY' && !carrierName.trim()) {
      setError('Carrier name is required for third-party shipments');
      return;
    }

    // Validate customer pickup fields
    if (shipmentType === 'CUSTOMER_PICKUP' && !pickedUpBy.trim()) {
      setError('Please specify who picked up the order');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/shipments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          orderId: selectedOrder.id,
          customerId: selectedOrder.customer.id,
          shipmentType,
          shipDate,
          deliveryAddress: deliveryAddress || null,
          deliveryCity: deliveryCity || null,
          deliveryState: deliveryState || null,
          deliveryZip: deliveryZip || null,
          sourceTankId: selectedTank?.id || null,
          // Applied to every line. Per-line origin (a two-rack load) is
          // corrected on the detail page afterwards; it is rare enough that
          // putting it on this form would cost more than it saves.
          originTerminalId: originTerminalId || null,
          // Internal fleet fields
          truckId: shipmentType === 'INTERNAL' ? (selectedTruck?.id || null) : null,
          driverId: shipmentType === 'INTERNAL' ? (selectedDriver?.id || null) : null,
          // Third-party carrier fields
          carrierName: shipmentType === 'THIRD_PARTY' ? (carrierName || null) : null,
          carrierBol: shipmentType === 'THIRD_PARTY' ? (carrierBol || null) : null,
          externalDriver: shipmentType === 'THIRD_PARTY' ? (externalDriver || null) : null,
          externalVehicle: shipmentType === 'THIRD_PARTY' ? (externalVehicle || null) : null,
          // Customer pickup field
          pickedUpBy: shipmentType === 'CUSTOMER_PICKUP' ? (pickedUpBy || null) : null,
          notes: notes || null,
          items: shipmentItems
            .filter((item) => item.shippedQuantity > 0)
            .map((item) => ({
              productId: item.productId,
              orderedQuantity: item.orderedQuantity,
              shippedQuantity: item.shippedQuantity,
              unitPrice: item.unitPrice,
            })),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create shipment');
      }

      const shipment = await response.json();
      navigate(`/shipments/${shipment.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create shipment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTotalQuantity = (order: Order) => {
    return order.items.reduce((sum, item) => sum + (item.remainingQuantity ?? item.quantity), 0);
  };

  const getTotalShipped = () => {
    return shipmentItems.reduce((sum, item) => sum + item.shippedQuantity, 0);
  };

  const getTotalValue = () => {
    return shipmentItems.reduce((sum, item) => sum + item.shippedQuantity * item.unitPrice, 0);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT':
        return 'text-error-red bg-error-red/20 border-error-red/50';
      case 'HIGH':
        return 'text-amber bg-amber/20 border-amber/50';
      case 'NORMAL':
        return 'text-steel bg-steel/20 border-steel/50';
      default:
        return 'text-text-tertiary bg-panel-border/20 border-panel-border/50';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex items-center space-x-4">
          <div className="w-3 h-3 bg-amber animate-pulse" />
          <span className="text-text-secondary font-mono">Loading shipment data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                New Shipment
              </h1>
              <div className="flex items-center space-x-4 mt-2">
                <Link
                  to="/shipments"
                  className="text-text-secondary text-sm font-mono hover:text-amber transition-colors"
                >
                  &larr; Back to Shipments
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-error-red/10 border border-error-red/50">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-error-red" />
            <p className="text-error-red text-sm font-mono">&gt; Error: {error}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Shipment Type Selection */}
        <FormSection title="Shipment Type" icon={Truck}>
          <div className="grid grid-cols-3 gap-4">
            <button
              type="button"
              onClick={() => setShipmentType('INTERNAL')}
              className={`p-4 border transition-all text-center ${
                shipmentType === 'INTERNAL'
                  ? 'border-amber bg-amber/10'
                  : 'border-panel-border bg-panel-dark hover:border-steel'
              }`}
            >
              <Truck className={`w-8 h-8 mx-auto mb-2 ${shipmentType === 'INTERNAL' ? 'text-amber' : 'text-steel'}`} />
              <p className="font-mono text-sm text-text-primary">Internal Fleet</p>
              <p className="text-text-tertiary text-xs mt-1">Our trucks & drivers</p>
            </button>
            <button
              type="button"
              onClick={() => setShipmentType('THIRD_PARTY')}
              className={`p-4 border transition-all text-center ${
                shipmentType === 'THIRD_PARTY'
                  ? 'border-amber bg-amber/10'
                  : 'border-panel-border bg-panel-dark hover:border-steel'
              }`}
            >
              <ExternalLink className={`w-8 h-8 mx-auto mb-2 ${shipmentType === 'THIRD_PARTY' ? 'text-amber' : 'text-steel'}`} />
              <p className="font-mono text-sm text-text-primary">Third-Party Carrier</p>
              <p className="text-text-tertiary text-xs mt-1">Contract hauler</p>
            </button>
            <button
              type="button"
              onClick={() => setShipmentType('CUSTOMER_PICKUP')}
              className={`p-4 border transition-all text-center ${
                shipmentType === 'CUSTOMER_PICKUP'
                  ? 'border-amber bg-amber/10'
                  : 'border-panel-border bg-panel-dark hover:border-steel'
              }`}
            >
              <Building2 className={`w-8 h-8 mx-auto mb-2 ${shipmentType === 'CUSTOMER_PICKUP' ? 'text-amber' : 'text-steel'}`} />
              <p className="font-mono text-sm text-text-primary">Customer Pickup</p>
              <p className="text-text-tertiary text-xs mt-1">Customer picks up</p>
            </button>
          </div>
        </FormSection>

        {/* Order Selection */}
        <FormSection title="Select Order" icon={ShoppingCart}>
          {orders.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-panel-border">
              <p className="text-text-tertiary font-mono">No orders available for shipment</p>
              <Link
                to="/orders/new"
                className="text-amber text-sm font-mono hover:underline mt-2 inline-block"
              >
                Create a new order &rarr;
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {orders.map((order) => (
                <button
                  key={order.id}
                  type="button"
                  onClick={() => setSelectedOrder(order)}
                  className={`text-left p-4 border transition-all ${
                    selectedOrder?.id === order.id
                      ? 'border-amber bg-amber/10'
                      : 'border-panel-border bg-panel-dark hover:border-steel'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-amber font-mono text-sm">{order.orderNumber}</span>
                    <span
                      className={`px-2 py-0.5 text-xs font-mono border ${getPriorityColor(order.priority)}`}
                    >
                      {order.priority}
                    </span>
                  </div>
                  <p className="text-text-primary font-medium mb-1">{order.customer.name}</p>
                  <p className="text-text-tertiary text-xs font-mono mb-2">
                    {order.deliveryCity}, {order.deliveryState}
                  </p>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-text-secondary">
                      <Package className="w-3 h-3 inline mr-1" />
                      {getTotalQuantity(order).toLocaleString()} gal remaining
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </FormSection>

        {/* Shipment Items */}
        {selectedOrder && shipmentItems.length > 0 && (
          <FormSection title="Shipment Items" icon={Package}>
            <div className="space-y-4">
              <div className="grid grid-cols-12 gap-4 text-xs font-mono uppercase text-text-tertiary tracking-wider pb-2 border-b border-panel-border">
                <div className="col-span-5">Product</div>
                <div className="col-span-2 text-right">Ordered</div>
                <div className="col-span-3 text-center">Ship Qty</div>
                <div className="col-span-2 text-right">Line Total</div>
              </div>

              {shipmentItems.map((item, index) => (
                <div key={item.productId} className="grid grid-cols-12 gap-4 items-center py-2">
                  <div className="col-span-5">
                    <p className="text-text-primary text-sm">{item.productName}</p>
                    <p className="text-text-tertiary text-xs font-mono">
                      ${item.unitPrice.toFixed(2)} / {item.unit}
                    </p>
                  </div>
                  <div className="col-span-2 text-right">
                    <p className="text-text-secondary text-sm">
                      {item.orderedQuantity.toLocaleString()} {item.unit}
                    </p>
                  </div>
                  <div className="col-span-3 flex items-center justify-center space-x-2">
                    <button
                      type="button"
                      onClick={() => handleItemQuantityChange(index, item.shippedQuantity - 100)}
                      className="p-1 bg-panel-border hover:bg-steel/20 text-text-secondary hover:text-steel transition-all"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <input
                      type="number"
                      value={item.shippedQuantity}
                      onChange={(e) =>
                        handleItemQuantityChange(index, parseFloat(e.target.value) || 0)
                      }
                      className="w-24 px-2 py-1 bg-background border border-panel-border text-text-primary text-center font-mono text-sm focus:outline-none focus:border-amber"
                    />
                    <button
                      type="button"
                      onClick={() => handleItemQuantityChange(index, item.shippedQuantity + 100)}
                      className="p-1 bg-panel-border hover:bg-steel/20 text-text-secondary hover:text-steel transition-all"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="col-span-2 text-right">
                    <p className="text-amber font-mono text-sm">
                      ${(item.shippedQuantity * item.unitPrice).toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}

              <div className="flex items-center justify-end pt-4 border-t border-panel-border space-x-8">
                <div className="text-right">
                  <p className="text-text-tertiary text-xs uppercase font-mono">Total Quantity</p>
                  <p className="text-text-primary font-mono text-lg">
                    {getTotalShipped().toLocaleString()} gal
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-text-tertiary text-xs uppercase font-mono">Total Value</p>
                  <p className="text-amber font-mono text-lg">${getTotalValue().toFixed(2)}</p>
                </div>
              </div>
            </div>
          </FormSection>
        )}

        {/* Source Tank */}
        <FormSection title="Source Tank (Optional)" icon={Database}>
          {tanks.length === 0 ? (
            <div className="text-center py-4 border border-dashed border-panel-border">
              <p className="text-text-tertiary font-mono text-sm">No tanks available</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {tanks.map((tank) => (
                <button
                  key={tank.id}
                  type="button"
                  onClick={() => setSelectedTank(selectedTank?.id === tank.id ? null : tank)}
                  className={`text-center p-4 border transition-all ${
                    selectedTank?.id === tank.id
                      ? 'border-amber bg-amber/10'
                      : 'border-panel-border bg-panel-dark hover:border-steel'
                  }`}
                >
                  <Database
                    className={`w-8 h-8 mx-auto mb-2 ${selectedTank?.id === tank.id ? 'text-amber' : 'text-steel'}`}
                  />
                  <p className="text-text-primary font-mono text-sm">{tank.tankNumber}</p>
                  <p className="text-text-tertiary text-xs truncate">{tank.name}</p>
                  <p className="text-text-secondary text-xs mt-1">
                    {tank.currentLevel.toLocaleString()} / {tank.capacity.toLocaleString()}
                  </p>
                </button>
              ))}
            </div>
          )}
        </FormSection>

        {/*
          "Lifted at" — the terminal this load came from.

          Origin decides which state's tax applies: a load lifted at a Texas
          rack and delivered into Louisiana owes Louisiana. Until now it could
          only be recorded AFTER the shipment existed, on the detail page — so
          the person actually holding the BOL, at the moment they are keying the
          shipment, had nowhere to put it, and the tax engine fell back to
          assuming the fuel never crossed a state line.

          Deliberately optional and deliberately blank by default. A load out of
          the distributor's own tank has no lifting terminal, and a shipment
          whose paperwork has not arrived should record nothing rather than a
          guess — an origin that looks confirmed but was assumed is worse than
          a visibly missing one.
        */}
        <FormSection title="Lifted At (Optional)" icon={Fuel}>
          {terminals.length === 0 ? (
            <div className="text-center py-4 border border-dashed border-panel-border">
              <p className="text-text-tertiary font-mono text-sm">
                No terminals on file
              </p>
              <Link
                to="/terminals"
                className="text-amber text-xs font-mono hover:underline mt-2 inline-block"
              >
                Add a terminal &rarr;
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              <select
                value={originTerminalId}
                onChange={(e) => setOriginTerminalId(e.target.value)}
                className="form-input"
              >
                <option value="">Not recorded</option>
                {terminals.map((terminal) => (
                  <option key={terminal.id} value={terminal.id}>
                    {terminal.name}
                    {terminal.state ? ` — ${terminal.city ? `${terminal.city}, ` : ''}${terminal.state}` : ''}
                  </option>
                ))}
              </select>
              <p className="text-text-tertiary text-xs font-mono">
                &gt; Where the fuel was lifted. Applied to every line on this
                shipment. Tax follows the movement, so a load lifted out of
                state is taxed by the state it is delivered to — leave this
                blank rather than guessing, and the invoice will say the origin
                was assumed.
              </p>
              {originTerminalId &&
                deliveryState &&
                terminals.find((t) => t.id === originTerminalId)?.state &&
                terminals.find((t) => t.id === originTerminalId)?.state !==
                  deliveryState.toUpperCase() && (
                  <div className="flex items-start space-x-2 p-3 bg-steel/10 border border-steel/40">
                    <AlertTriangle className="w-4 h-4 text-steel mt-0.5 flex-shrink-0" />
                    <p className="text-text-secondary text-xs font-mono">
                      Cross-state movement:{' '}
                      {terminals.find((t) => t.id === originTerminalId)?.state} &rarr;{' '}
                      {deliveryState.toUpperCase()}. This load will be taxed differently
                      from an in-state one.
                    </p>
                  </div>
                )}
            </div>
          )}
        </FormSection>

        {/* Internal Fleet - Truck Selection */}
        {shipmentType === 'INTERNAL' && (
          <FormSection title="Assign Truck (Optional)" icon={Truck}>
            {trucks.length === 0 ? (
              <div className="text-center py-4 border border-dashed border-panel-border">
                <p className="text-text-tertiary font-mono text-sm">No trucks available</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {trucks.map((truck) => (
                  <button
                    key={truck.id}
                    type="button"
                    onClick={() => setSelectedTruck(selectedTruck?.id === truck.id ? null : truck)}
                    className={`text-center p-4 border transition-all ${
                      selectedTruck?.id === truck.id
                        ? 'border-amber bg-amber/10'
                        : 'border-panel-border bg-panel-dark hover:border-steel'
                    }`}
                  >
                    <Truck
                      className={`w-8 h-8 mx-auto mb-2 ${selectedTruck?.id === truck.id ? 'text-amber' : 'text-steel'}`}
                    />
                    <p className="text-text-primary font-mono text-sm">{truck.unitNumber}</p>
                    {truck.name && <p className="text-text-tertiary text-xs">{truck.name}</p>}
                    {truck.totalCapacity && (
                      <p className="text-text-secondary text-xs mt-1">
                        {truck.totalCapacity.toLocaleString()} gal
                      </p>
                    )}
                  </button>
                ))}
              </div>
            )}
          </FormSection>
        )}

        {/* Internal Fleet - Driver Selection */}
        {shipmentType === 'INTERNAL' && (
          <FormSection title="Assign Driver (Optional)" icon={User}>
            {drivers.length === 0 ? (
              <div className="text-center py-4 border border-dashed border-panel-border">
                <p className="text-text-tertiary font-mono text-sm">No drivers available</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {drivers.map((driver) => (
                  <button
                    key={driver.id}
                    type="button"
                    onClick={() =>
                      setSelectedDriver(selectedDriver?.id === driver.id ? null : driver)
                    }
                    className={`text-center p-4 border transition-all ${
                      selectedDriver?.id === driver.id
                        ? 'border-amber bg-amber/10'
                        : 'border-panel-border bg-panel-dark hover:border-steel'
                    }`}
                  >
                    <User
                      className={`w-8 h-8 mx-auto mb-2 ${selectedDriver?.id === driver.id ? 'text-amber' : 'text-steel'}`}
                    />
                    <p className="text-text-primary font-mono text-sm">{driver.driverNumber}</p>
                    <p className="text-text-secondary text-xs">
                      {driver.firstName} {driver.lastName}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </FormSection>
        )}

        {/* Third-Party Carrier Fields */}
        {shipmentType === 'THIRD_PARTY' && (
          <FormSection title="Third-Party Carrier" icon={ExternalLink}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Carrier Name <span className="text-amber">*</span>
                </label>
                <input
                  type="text"
                  value={carrierName}
                  onChange={(e) => setCarrierName(e.target.value)}
                  placeholder="e.g., ABC Trucking"
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Carrier BOL Number
                </label>
                <input
                  type="text"
                  value={carrierBol}
                  onChange={(e) => setCarrierBol(e.target.value)}
                  placeholder="Carrier's BOL #"
                  className="form-input"
                />
              </div>
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  External Driver Name
                </label>
                <input
                  type="text"
                  value={externalDriver}
                  onChange={(e) => setExternalDriver(e.target.value)}
                  placeholder="Driver's name"
                  className="form-input"
                />
              </div>
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Vehicle/Trailer ID
                </label>
                <input
                  type="text"
                  value={externalVehicle}
                  onChange={(e) => setExternalVehicle(e.target.value)}
                  placeholder="Vehicle or trailer #"
                  className="form-input"
                />
              </div>
            </div>
          </FormSection>
        )}

        {/* Customer Pickup Fields */}
        {shipmentType === 'CUSTOMER_PICKUP' && (
          <FormSection title="Customer Pickup" icon={Building2}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Picked Up By <span className="text-amber">*</span>
                </label>
                <input
                  type="text"
                  value={pickedUpBy}
                  onChange={(e) => setPickedUpBy(e.target.value)}
                  placeholder="Name of person picking up"
                  className="form-input"
                  required
                />
              </div>
              <div className="flex items-end">
                <p className="text-text-tertiary text-sm font-mono">
                  Customer will pick up the product at our location
                </p>
              </div>
            </div>
          </FormSection>
        )}

        {/* Shipment Details */}
        <FormSection title="Shipment Details" icon={Calendar}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Ship Date <span className="text-amber">*</span>
              </label>
              <input
                type="date"
                value={shipDate}
                onChange={(e) => setShipDate(e.target.value)}
                required
                className="form-input"
              />
            </div>

            <div className="lg:col-span-2">
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Delivery Address
              </label>
              <input
                type="text"
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
                placeholder="Street address"
                className="form-input"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                City
              </label>
              <input
                type="text"
                value={deliveryCity}
                onChange={(e) => setDeliveryCity(e.target.value)}
                placeholder="City"
                className="form-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  State
                </label>
                <input
                  type="text"
                  value={deliveryState}
                  onChange={(e) => setDeliveryState(e.target.value)}
                  placeholder="ST"
                  maxLength={2}
                  className="form-input"
                />
              </div>
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  ZIP
                </label>
                <input
                  type="text"
                  value={deliveryZip}
                  onChange={(e) => setDeliveryZip(e.target.value)}
                  placeholder="ZIP"
                  className="form-input"
                />
              </div>
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Shipment notes..."
              className="form-input resize-none"
            />
          </div>
        </FormSection>

        {/* Summary Panel */}
        {selectedOrder && (
          <div className="bg-panel-dark border border-amber/30 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <FileText className="w-5 h-5 text-amber" />
              <h3 className="text-lg font-display text-amber uppercase tracking-wide">
                Shipment Summary
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <p className="text-text-tertiary text-xs uppercase font-mono mb-1">Order</p>
                <p className="text-text-primary font-mono">{selectedOrder.orderNumber}</p>
                <p className="text-text-secondary text-sm">{selectedOrder.customer.name}</p>
              </div>
              <div>
                <p className="text-text-tertiary text-xs uppercase font-mono mb-1">Ship To</p>
                <p className="text-text-primary text-sm">
                  {deliveryCity || selectedOrder.deliveryCity},{' '}
                  {deliveryState || selectedOrder.deliveryState}
                </p>
              </div>
              <div>
                <p className="text-text-tertiary text-xs uppercase font-mono mb-1">Total Qty</p>
                <p className="text-text-primary font-mono">
                  {getTotalShipped().toLocaleString()} gal
                </p>
              </div>
              <div>
                <p className="text-text-tertiary text-xs uppercase font-mono mb-1">Total Value</p>
                <p className="text-amber font-mono">${getTotalValue().toFixed(2)}</p>
              </div>
            </div>
            {(selectedTruck || selectedDriver || selectedTank) && (
              <div className="mt-4 pt-4 border-t border-panel-border grid grid-cols-3 gap-4">
                {selectedTank && (
                  <div className="flex items-center space-x-2">
                    <Database className="w-4 h-4 text-steel" />
                    <span className="text-text-tertiary text-sm">
                      Source: {selectedTank.tankNumber}
                    </span>
                  </div>
                )}
                {selectedTruck && (
                  <div className="flex items-center space-x-2">
                    <Truck className="w-4 h-4 text-amber" />
                    <span className="text-text-tertiary text-sm">{selectedTruck.unitNumber}</span>
                  </div>
                )}
                {selectedDriver && (
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4 text-steel" />
                    <span className="text-text-tertiary text-sm">
                      {selectedDriver.firstName} {selectedDriver.lastName}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-end space-x-4 pt-4">
          <button
            type="button"
            onClick={() => navigate('/shipments')}
            className="px-6 py-3 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm tracking-wider transition-all border border-panel-border hover:border-steel"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={isSubmitting || !selectedOrder || shipmentItems.length === 0}
            className="px-8 py-3 bg-amber hover:bg-amber/90 text-background font-display font-bold uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed relative group"
            style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
          >
            <span className="relative z-10">
              {isSubmitting ? 'Creating...' : 'Create Shipment'}
            </span>
            <div
              className="absolute inset-0 bg-gradient-to-r from-amber to-steel opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ mixBlendMode: 'overlay' }}
            />
          </button>
        </div>
      </form>
    </div>
  );
};

function FormSection({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
}) {
  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
        <div className="flex items-center space-x-3 mb-6">
          <Icon className="w-6 h-6 text-amber" />
          <h3 className="text-xl font-display text-text-primary uppercase tracking-wide">
            {title}
          </h3>
        </div>
        {children}
      </div>
    </div>
  );
}

export default NewShipmentPage;
